import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import { API_BASE } from "@/services/api";
import {
  Upload, FileText, CheckCircle2, Trash2, BarChart3, Settings, Loader2,
  ArrowLeft, Star, Eye, Download, MousePointerClick, Rocket,
} from "lucide-react";

const ADMIN_EMAILS = ["hariravi28@gmail.com", "venkiganesh903@gmail.com"];
const TABS = [
  { id: "resources", label: "PDF Resources", icon: FileText },
  { id: "content", label: "Landing Content", icon: Settings },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
];

const Metric = ({ icon: Icon, label, value, sub }) => (
  <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-5">
    <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wide mb-2">
      <Icon className="w-4 h-4 text-orange-500" /> {label}
    </div>
    <div className="text-2xl font-bold text-white">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

export default function FreeAnswerAdminPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const isAdmin = user && ADMIN_EMAILS.includes(user.email?.toLowerCase());

  const [tab, setTab] = useState("resources");
  const [resources, setResources] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const loadResources = useCallback(async () => {
    const r = await axios.get(`${API_BASE}/free-answer/admin/resources`, auth);
    setResources(r.data.resources || []);
  }, [token]);

  const loadConfig = useCallback(async () => {
    const r = await axios.get(`${API_BASE}/free-answer/admin/config`, auth);
    setCfg(r.data);
  }, [token]);

  const loadAnalytics = useCallback(async () => {
    const r = await axios.get(`${API_BASE}/free-answer/admin/analytics?days=30`, auth);
    setAnalytics(r.data);
  }, [token]);

  useEffect(() => {
    if (!isAdmin) return;
    loadResources().catch(() => {});
    loadConfig().catch(() => {});
    loadAnalytics().catch(() => {});
  }, [isAdmin, loadResources, loadConfig, loadAnalytics]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 3000); };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API_BASE}/free-answer/admin/resources?title=${encodeURIComponent(file.name)}`,
        fd, { headers: { ...auth.headers, "Content-Type": "multipart/form-data" } });
      await loadResources();
      flash("PDF uploaded successfully.");
    } catch (err) {
      flash(err.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const activate = async (id) => {
    await axios.post(`${API_BASE}/free-answer/admin/resources/${id}/activate`, {}, auth);
    await loadResources();
    flash("Active sample answer updated.");
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this PDF? This cannot be undone.")) return;
    await axios.delete(`${API_BASE}/free-answer/admin/resources/${id}`, auth);
    await loadResources();
    flash("PDF deleted.");
  };

  const saveConfig = async () => {
    setBusy(true);
    try {
      await axios.put(`${API_BASE}/free-answer/admin/config`, cfg, auth);
      flash("Landing content saved.");
    } catch {
      flash("Save failed.");
    } finally { setBusy(false); }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center text-center p-6">
        <div>
          <h2 className="text-xl text-white mb-3">Admin access required</h2>
          <button onClick={() => navigate("/admin")} className="text-orange-400 underline">Back to Admin</button>
        </div>
      </div>
    );
  }

  const fld = "w-full rounded-lg bg-slate-800 border border-slate-700 text-white px-3 py-2 text-sm focus:border-orange-500 outline-none";

  return (
    <div className="min-h-screen bg-slate-900 p-4 md:p-8" data-testid="free-answer-admin">
      <div className="max-w-6xl mx-auto">
        <button onClick={() => navigate("/admin")} className="text-slate-400 hover:text-white text-sm flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Admin
        </button>
        <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3 mb-1">
          <Rocket className="w-7 h-7 text-orange-500" /> Free Answer Funnel
        </h1>
        <p className="text-slate-400 mb-6">Manage the /free-answer landing page, PDF and Instagram campaign analytics.</p>

        {msg && <div className="mb-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-sm" data-testid="admin-flash">{msg}</div>}

        <div className="flex gap-2 mb-6 border-b border-slate-800">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? "border-orange-500 text-white" : "border-transparent text-slate-400 hover:text-white"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* ---------------------------------------------------- Resources */}
        {tab === "resources" && (
          <div>
            <label data-testid="upload-pdf-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-5 py-3 font-semibold cursor-pointer mb-6">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
              Upload PDF
              <input type="file" accept="application/pdf" className="hidden" onChange={onUpload} disabled={busy} />
            </label>
            <p className="text-xs text-slate-500 mb-4">The active PDF is what visitors download. Only PDFs, max 25 MB.</p>
            <div className="space-y-3">
              {resources.length === 0 && <p className="text-slate-500">No PDFs uploaded yet.</p>}
              {resources.map((r) => (
                <div key={r.id} data-testid={`resource-row-${r.id}`}
                  className={`flex items-center justify-between gap-4 rounded-xl border p-4 ${r.is_active ? "border-orange-500/50 bg-orange-500/5" : "border-slate-700 bg-slate-800/50"}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-6 h-6 text-orange-500 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-white text-sm font-medium truncate">{r.title || r.original_filename}</p>
                      <p className="text-xs text-slate-500">{(r.size / 1024 / 1024).toFixed(1)} MB · {r.original_filename}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {r.is_active ? (
                      <span className="inline-flex items-center gap-1 text-emerald-400 text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-500/10">
                        <Star className="w-3.5 h-3.5 fill-emerald-400" /> Active
                      </span>
                    ) : (
                      <button onClick={() => activate(r.id)} data-testid={`activate-${r.id}`}
                        className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-white">
                        Set Active
                      </button>
                    )}
                    <button onClick={() => remove(r.id)} data-testid={`delete-${r.id}`}
                      className="p-2 rounded-lg bg-slate-700 hover:bg-red-500/20 text-slate-300 hover:text-red-400">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ------------------------------------------------------ Content */}
        {tab === "content" && cfg && (
          <div className="space-y-5 max-w-2xl">
            {[
              ["hero_title", "Hero Title"],
              ["hero_subtitle", "Hero Subtitle"],
              ["primary_cta", "Primary Button Text"],
              ["secondary_cta", "Secondary Button Text"],
              ["hero_image_url", "Hero Image URL"],
              ["about_text", "About Text"],
              ["about_cta", "About CTA Text"],
            ].map(([key, label]) => (
              <div key={key}>
                <label className="block text-sm text-slate-400 mb-1">{label}</label>
                {key === "hero_subtitle" || key === "about_text" ? (
                  <textarea rows={3} className={fld} value={cfg[key] || ""} data-testid={`cfg-${key}`}
                    onChange={(e) => setCfg({ ...cfg, [key]: e.target.value })} />
                ) : (
                  <input className={fld} value={cfg[key] || ""} data-testid={`cfg-${key}`}
                    onChange={(e) => setCfg({ ...cfg, [key]: e.target.value })} />
                )}
              </div>
            ))}
            <button onClick={saveConfig} disabled={busy} data-testid="save-config-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 font-semibold disabled:opacity-50">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />} Save Changes
            </button>
            <p className="text-xs text-slate-500">Testimonials & FAQ editing coming next — the funnel uses sensible defaults meanwhile.</p>
          </div>
        )}

        {/* ---------------------------------------------------- Analytics */}
        {tab === "analytics" && analytics && (
          <div data-testid="analytics-panel">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Metric icon={Eye} label="Page Views" value={analytics.totals.page_views} sub="last 30 days" />
              <Metric icon={Download} label="Downloads" value={analytics.totals.downloads} sub={`${analytics.conversion_rate}% conversion`} />
              <Metric icon={MousePointerClick} label="Explore Clicks" value={analytics.totals.explore_clicks} />
              <Metric icon={Rocket} label="Install Clicks" value={analytics.totals.install_clicks} sub={`${analytics.install_rate}% of views`} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[["by_source", "Referral Source (UTM)"], ["by_campaign", "Instagram Campaign"], ["by_device", "Device"], ["by_country", "Country"]].map(([k, label]) => (
                <div key={k} className="rounded-xl border border-slate-700 bg-slate-800/50 p-5">
                  <p className="text-sm font-semibold text-white mb-3">{label}</p>
                  {(analytics[k] || []).length === 0 && <p className="text-xs text-slate-500">No data yet.</p>}
                  {(analytics[k] || []).map((row) => (
                    <div key={row.key} className="flex justify-between text-sm py-1 border-b border-slate-700/50 last:border-0">
                      <span className="text-slate-300 truncate">{row.key}</span>
                      <span className="text-slate-400 font-mono">{row.count}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
